import React from 'react';
import { Switch, Tag, Tooltip, Typography, theme } from 'antd';
import { audienceLabel, countryFlag, istClock } from '@/utils/cricketCalendar';
import type { CalendarFestival, CricketFixture } from '@/types/matchPushes';

const { Text } = Typography;

const matchTitle = (f: CricketFixture) => `${f.team1Name} vs ${f.team2Name}`;

const flags = (codes: string[]) => codes.map(countryFlag).filter(Boolean).join(' ') || undefined;

interface FixtureRowProps {
  fixture: CricketFixture;
  selected: boolean;
  /** True while this row's tracked switch is saving. */
  saving: boolean;
  /** False for show-only admins: the tracked switch is disabled. */
  canChange: boolean;
  onSelect: () => void;
  onToggleTracked: (tracked: boolean) => void;
}

/** One match: IST time, teams and label, series and venue, tags, audience, tracked switch. */
export const FixtureRow: React.FC<FixtureRowProps> = ({
  fixture,
  selected,
  saving,
  canChange,
  onSelect,
  onToggleTracked,
}) => {
  const { token } = theme.useToken();
  const cancelled = fixture.status === 'cancelled';
  const dim = !fixture.tracked || cancelled;
  const pushes = fixture.plans?.total ?? 0;
  const events = fixture.liveEvents?.length ?? 0;
  const flagText = flags(fixture.audienceCountries);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
      style={{
        display: 'grid',
        gridTemplateColumns: '56px minmax(0, 1fr) 150px 96px',
        gap: 10,
        alignItems: 'center',
        padding: '9px 12px',
        marginBottom: 6,
        borderRadius: 8,
        cursor: 'pointer',
        border: `1px solid ${selected ? token.colorPrimary : token.colorBorderSecondary}`,
        background: selected ? token.colorPrimaryBg : token.colorBgContainer,
        opacity: dim && !selected ? 0.65 : 1,
      }}
    >
      <Text strong>{istClock(fixture.startsAt)}</Text>
      <div style={{ minWidth: 0 }}>
        <div>
          <Text strong delete={cancelled}>
            {matchTitle(fixture)}
          </Text>
          {fixture.matchLabel && <Text> · {fixture.matchLabel}</Text>}
        </div>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {[fixture.seriesName, fixture.venue].filter(Boolean).join(' · ') || 'No series'}
        </Text>
        <div>
          {fixture.status === 'live' && <Tag color="red">Live</Tag>}
          {cancelled && <Tag>Cancelled</Tag>}
          {fixture.status === 'finished' && <Tag>Finished</Tag>}
          {events > 0 && (
            <Tag color="blue">
              {events} live event{events === 1 ? '' : 's'}
            </Tag>
          )}
          {pushes > 0 && (
            <Tag>
              {pushes} push{pushes === 1 ? '' : 'es'}
            </Tag>
          )}
          {fixture.source === 'manual' && <Tag color="orange">Added by hand</Tag>}
        </div>
      </div>
      <Text
        type="secondary"
        style={{ fontSize: 12 }}
        title={audienceLabel(fixture.audienceCountries)}
      >
        {flagText ? `${flagText} ${audienceLabel(fixture.audienceCountries)}` : '—'}
      </Text>
      <div
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        style={{ display: 'flex', gap: 6 }}
      >
        <Tooltip title={canChange ? undefined : 'You can only view Live Ops'}>
          <Switch
            size="small"
            checked={fixture.tracked}
            loading={saving}
            disabled={cancelled || !canChange}
            onChange={onToggleTracked}
            aria-label={`Track ${matchTitle(fixture)}`}
          />
        </Tooltip>
        <Text style={{ fontSize: 12 }}>{fixture.tracked ? 'Tracked' : 'Off'}</Text>
      </div>
    </div>
  );
};

interface FestivalRowProps {
  festival: CalendarFestival;
  /** False for show-only admins: the row only shows. */
  canChange: boolean;
  onEdit: () => void;
}

/** A festival on its first day: dashed row, click to edit. */
export const FestivalRow: React.FC<FestivalRowProps> = ({ festival, canChange, onEdit }) => {
  const { token } = theme.useToken();
  const range =
    festival.endsOn && festival.endsOn !== festival.startsOn ? ` · until ${festival.endsOn}` : '';
  return (
    <div
      role={canChange ? 'button' : undefined}
      tabIndex={canChange ? 0 : undefined}
      onClick={canChange ? onEdit : undefined}
      onKeyDown={(e) => {
        if (canChange && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onEdit();
        }
      }}
      style={{
        display: 'grid',
        gridTemplateColumns: '56px minmax(0, 1fr) 150px 96px',
        gap: 10,
        alignItems: 'center',
        padding: '9px 12px',
        marginBottom: 6,
        borderRadius: 8,
        cursor: canChange ? 'pointer' : 'default',
        border: `1px dashed ${token.colorBorder}`,
        background: token.colorBgContainer,
      }}
    >
      <Text type="secondary">All day</Text>
      <div style={{ minWidth: 0 }}>
        <Text strong>{festival.name}</Text>
        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Festival{range}
            {festival.regions.length ? ` · ${festival.regions.join(', ')}` : ''}
            {festival.notes ? ` · ${festival.notes}` : ''}
          </Text>
        </div>
      </div>
      <Text type="secondary" style={{ fontSize: 12 }}>
        {flags(festival.countries) ?? '—'}
      </Text>
      <Text type="secondary" style={{ fontSize: 12 }}>
        Festival
      </Text>
    </div>
  );
};
